import dayjs from 'dayjs';
import { ComponentList } from '../../components/core/component-list.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { TimeZone } from '../../time/timezone.js';
import { DataModel, SerializationContext, DataModelBuilder, SerializedData, isSameData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { NotificationType } from './notification-type.js';

export enum NotificationStatus {
    Upcoming = 'UPCOMING',
    Sent = 'SENT',
    Cancelled = 'CANCELLED',
}

export class CloudNotification extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '44dbee2a946d4a8582f90c673e5770f3';

  constructor(
        public readonly uniqueId: string,
        public readonly status: NotificationStatus,
        public readonly type: NotificationType,
        public readonly groupId: string,
        public readonly templateId: string,
        public readonly creatorId: string | null,
        public readonly creationDate: dayjs.Dayjs,
        public readonly expirationDate: dayjs.Dayjs,
        public readonly components: ComponentList,
  ) {
    super();
  }

  get $typeId(): string {
    return CloudNotification.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): CloudNotification {
    return new CloudNotification(
        MapUtils.get<string>(map, 'uniqueId'),
        toEnumFromString(MapUtils.get<string>(map, '$status'), NotificationStatus),
        toEnumFromString(MapUtils.get<string>(map, 'type'), NotificationType),
        MapUtils.get<string>(map, 'groupId'),
        MapUtils.get<string>(map, 'templateId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        context.decodeDate(MapUtils.get<number>(map, 'expirationDate')),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      '$status': fromEnumAsString(this.status),
      'type': fromEnumAsString(this.type),
      'groupId': this.groupId,
      'templateId': this.templateId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'formattedCreationDate': this.creationDate.format('MMMM DD, YYYY HH:mm:ss Z'),
      'expirationDate': context.encodeDate(this.expirationDate),
      'formattedExpirationDate': this.expirationDate.format('MMMM DD, YYYY HH:mm:ss Z'),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new CloudNotification(
        this.uniqueId,
        this.status,
        this.type,
        this.groupId,
        this.templateId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.expirationDate),
        this.components.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.status === this.status &&
        other.type === this.type &&
        other.groupId === this.groupId &&
        other.templateId === this.templateId &&
        other.creatorId === this.creatorId &&
        other.creationDate === this.creationDate &&
        other.expirationDate === this.expirationDate &&
        isSameData(other.components, this.components);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new CloudNotification(
        this.uniqueId,
        this.status,
        this.type,
        this.groupId,
        this.templateId,
        this.creatorId,
        TimeZone.cloneDate(this.creationDate),
        TimeZone.cloneDate(this.expirationDate),
        this.components.copyWithBusinessRef(businessRef),
    ) as this;
  }
}

export class CloudNotificationBuilder extends DataModelBuilder<CloudNotification> {
  build(context: SerializationContext, map: SerializedData): CloudNotification {
    return CloudNotification.fromMap(context, map);
  }
}
